import { apiClient } from 'utils/apiClient';
import { ApiEndpoints } from 'models/apiEndpoints';
import { isResponseError, TResponse } from 'models/apiClient';
import { EPerPages, ISortOptions } from 'models/table';
import { queryString, queryStringWithSort } from 'helpers/request';
import {
  EDictionaryTypes,
  ETransactionDictionaryItemTypes,
  IAsyncDictionaryResponse,
  IDictionariesResponse,
  IDictionaryDetailResponse,
  IDictionaryLanguage,
  IDictionaryResponse,
  IDictionaryTypeHash,
  IDictionary,
  IDictionarySearchLike,
  IDictionaryUsage,
  IDictionaryValueFormValues,
  IDictionaryValues,
} from 'models/dictionary';
import axios from 'axios';
import { RequestHeaderValueTypes } from 'constants/RequestHeaderTypes';
import { FC_SYSTEM_APP_LANG } from 'requests/language';
import {
  buildDictionaryCacheKey,
  deleteCachedDictionary,
  getAllCachedDictionaries,
  getCachedDictionary,
  setCachedDictionary,
} from 'utils/dictionaryIndexedDb';

const getDictionaries = async (
  page: number,
  size: EPerPages,
  sort: ISortOptions | null | undefined,
  query: IDictionarySearchLike,
) => {
  const queryString = queryStringWithSort({ page: page - 1, size, ...query }, sort);
  const { data } = await apiClient.get<TResponse<IDictionariesResponse>>(
    `${ApiEndpoints.GET_DICTIONARIES()}?${queryString}`,
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getDictionaryDetail = async (
  type: EDictionaryTypes,
  page: number,
  size: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const queryString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.get<TResponse<IDictionaryDetailResponse>>(
    `${ApiEndpoints.GET_DICTIONARY_DETAIL(type)}?${queryString}`,
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getDictionaryFromApi = async (type: EDictionaryTypes): Promise<IDictionary[] | null> => {
  const { data } = await apiClient.get<TResponse<IDictionary[]>>(
    ApiEndpoints.GET_DICTIONARY(type),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getDictionaryTypeHashes = async (types: string[]): Promise<IDictionaryTypeHash[]> => {
  try {
    const { data } = await apiClient.post<TResponse<IDictionaryTypeHash[]>>(
      ApiEndpoints.GET_DICTIONARY_TYPE_HASHES(),
      { types },
    );
    if (isResponseError(data)) {
      return [];
    }
    return data;
  } catch {
    return [];
  }
};

const getCurrentLang = (): string => localStorage.getItem(FC_SYSTEM_APP_LANG) ?? '';

const buildTypeHashMap = (hashes: IDictionaryTypeHash[]): Record<string, string> =>
  hashes.reduce<Record<string, string>>((acc, { type, hash }) => {
    acc[type] = hash ?? '';
    return acc;
  }, {});

const fetchAndCacheDictionary = async (
  type: EDictionaryTypes,
  lang: string,
  hash: string,
): Promise<IDictionary[] | null> => {
  const items = await getDictionaryFromApi(type);
  if (!items) {
    return null;
  }

  await setCachedDictionary({
    key: buildDictionaryCacheKey(type, lang),
    type,
    lang,
    items,
    hash,
  });

  return items;
};

const revalidateCache = async (
  lang: string,
  types: string[],
): Promise<Record<string, string>> => {
  const typeHashes = buildTypeHashMap(await getDictionaryTypeHashes(types));
  const cached = (await getAllCachedDictionaries()).filter(
    (entry) => entry.lang === lang && types.includes(entry.type),
  );
  await Promise.all(
    cached
      .filter(
        (entry) =>
          typeHashes[entry.type] !== undefined &&
          typeHashes[entry.type] !== (entry.hash ?? ''),
      )
      .map((entry) => deleteCachedDictionary(entry.key)),
  );
  return typeHashes;
};

let pendingTypes = new Set<string>();
let pendingValidation: Promise<Record<string, string>> | null = null;

const validateCachedDictionaries = (
  lang: string,
  type: EDictionaryTypes,
): Promise<Record<string, string>> => {
  pendingTypes.add(type);
  if (!pendingValidation) {
    pendingValidation = Promise.resolve().then(() => {
      const types = Array.from(pendingTypes).sort();
      pendingTypes = new Set();
      pendingValidation = null;
      return revalidateCache(lang, types);
    });
  }
  return pendingValidation;
};

const getDictionary = async (type: EDictionaryTypes): Promise<IDictionary[] | null> => {
  const lang = getCurrentLang();
  try {
    const typeHashes = await validateCachedDictionaries(lang, type);
    const cached = await getCachedDictionary(buildDictionaryCacheKey(type, lang));
    if (cached?.items?.length) {
      return cached.items;
    }
    return await fetchAndCacheDictionary(type, lang, typeHashes[type] ?? '');
  } catch (error) {
    return getDictionaryFromApi(type);
  }
};

const getDictionaryNamesLike = async (type: EDictionaryTypes, body: IDictionarySearchLike) => {
  const { data } = await apiClient.post<TResponse<{ name: string[] }>>(
    ApiEndpoints.GET_DICTIONARY_LIKE(type),
    body,
  );

  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getDictionaryTypeLanguages = async (type: EDictionaryTypes) => {
  const { data } = await apiClient.get<TResponse<IDictionaryLanguage[]>>(
    ApiEndpoints.GET_DICTIONARY_LANGUAGES(type),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getDictionaryById = async (id: string) => {
  const { data } = await apiClient.get(ApiEndpoints.GET_DICTIONARY_BY_ID(id));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getDictionariesByIds = async (uniqueIds: string[]) => {
  const { data } = await apiClient.post<TResponse<IDictionary[]>>(
    ApiEndpoints.GET_DICTIONARIES_BY_IDS(),
    { uniqueIds },
  );

  if (isResponseError(data)) {
    return [];
  }

  return data;
};

const getDictionaryUsage = async (id: string) => {
  const { data } = await apiClient.get<TResponse<IDictionaryUsage>>(
    ApiEndpoints.GET_DICTIONARY_USAGE(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getTransactionInitialTypesDictionary = async (
  itemType: ETransactionDictionaryItemTypes,
) => {
  const { data } = await apiClient.get<TResponse<IDictionaryResponse>>(
    ApiEndpoints.GET_INITIAL_TRANSACTION_DICTIONARY(itemType),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getTransactionMiddleTypesDictionary = async (
  itemType: ETransactionDictionaryItemTypes,
) => {
  const { data } = await apiClient.get<TResponse<IDictionaryResponse>>(
    ApiEndpoints.GET_MIDDLE_TRANSACTION_DICTIONARY(itemType),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createDictionaryValue = async (
  body: IDictionaryValueFormValues,
  type: EDictionaryTypes,
) =>
  apiClient.post<TResponse<IDictionaryValues>>(
    ApiEndpoints.CREATE_DICTIONARY_VALUE(type),
    body,
  );

const updateDictionaryValue = async (body: IDictionaryValueFormValues, id: string) =>
  apiClient.put<TResponse<IDictionaryValues>>(ApiEndpoints.UPDATE_DICTIONARY_VALUE(id), body);

const getAsyncDictionary = async (
  name: EDictionaryTypes,
  page: number,
  size: number,
  query: string,
  signal?: AbortSignal,
  includeArchived = false,
) => {
  const qString = queryString({ page, size });
  const endpoint = includeArchived
    ? ApiEndpoints.GET_DICTIONARY_SEARCH_ALL(name)
    : ApiEndpoints.GET_DICTIONARY_SEARCH(name);
  try {
    const { data } = await apiClient.post<TResponse<IAsyncDictionaryResponse>>(
      `${endpoint}?${qString}`,
      { searchValue: query },
      { signal },
    );

    if (isResponseError(data)) return null;

    return data;
  } catch (error: any) {
    if (axios.isCancel(error)) return null;
    throw error;
  }
};

const getDictionaryTranslationTemplate = async () =>
  apiClient.get<Blob>(ApiEndpoints.GET_DICTIONARY_TRANSLATION_TEMPLATE(), {
    responseType: 'blob',
  });

const importDictionaryTranslations = async (formData: FormData) =>
  apiClient.post<void>(ApiEndpoints.IMPORT_DICTIONARY_TRANSLATIONS(), formData, {
    timeout: 600_000,
    headers: {
      'Content-Type': RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
  });

export {
  getDictionaries,
  getDictionaryDetail,
  getDictionary,
  getTransactionInitialTypesDictionary,
  getTransactionMiddleTypesDictionary,
  getDictionaryById,
  getDictionariesByIds,
  getDictionaryUsage,
  createDictionaryValue,
  updateDictionaryValue,
  getDictionaryTypeLanguages,
  getDictionaryNamesLike,
  getAsyncDictionary,
  getDictionaryTranslationTemplate,
  importDictionaryTranslations,
};
